'use strict';

/**
 * Webpack/web only: replaces @react-native-async-storage/async-storage.
 * RN package v3 ships strict ESM with extensionless subpath imports that break
 * in our webpack + splitChunks setup at runtime. Native/Metro still resolves
 * the real package (this file is only wired via webpack resolve.alias).
 */

const fallback = Object.create(null);

function getItemSync(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (_e) {
    return fallback[key] ?? null;
  }
}

function setItemSync(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (_e) {
    fallback[key] = String(value);
  }
}

function removeItemSync(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (_e) {
    delete fallback[key];
  }
}

const AsyncStorage = {
  getItem(key) {
    return Promise.resolve(getItemSync(key));
  },
  setItem(key, value) {
    return Promise.resolve(setItemSync(key, value));
  },
  removeItem(key) {
    return Promise.resolve(removeItemSync(key));
  },
  async mergeItem(key, value) {
    const existing = getItemSync(key);
    if (!existing) {
      setItemSync(key, value);
      return;
    }
    try {
      const a = JSON.parse(existing);
      const b = JSON.parse(value);
      setItemSync(key, JSON.stringify({...a, ...b}));
    } catch (_e) {
      setItemSync(key, value);
    }
  },
  clear() {
    try {
      window.localStorage.clear();
    } catch (_e) {
      for (const k of Object.keys(fallback)) {
        delete fallback[k];
      }
    }
    return Promise.resolve();
  },
  getAllKeys() {
    try {
      return Promise.resolve(Object.keys(window.localStorage));
    } catch (_e) {
      return Promise.resolve(Object.keys(fallback));
    }
  },
  multiGet(keys) {
    return Promise.all(
      keys.map(k => Promise.resolve([k, getItemSync(k)])),
    );
  },
  multiSet(keyValuePairs) {
    return Promise.all(
      keyValuePairs.map(([k, v]) => Promise.resolve(setItemSync(k, v))),
    );
  },
  multiRemove(keys) {
    return Promise.all(
      keys.map(k => Promise.resolve(removeItemSync(k))),
    );
  },
};

module.exports = AsyncStorage;
